import { useEffect, useMemo, useState } from "react";
import {
  gxBeneficiaryBulkApproveApi,
  gxBeneficiaryPendingListApi,
  gxBeneficiarySingleApproveApi,
  GxBeneficiaryCountryApi,
} from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import type { Beneficiary, GlobalCountry } from "@/app/types/beneficiaries";
import GxRedRejectButtonSmall from "@/app/components/common/actions/buttons/GxRedRejectButtonSmall";
import GxPrimarySmallButton from "@/app/components/common/actions/buttons/GxPrimarySmallButton";
import GxRedRejectButton from "@/app/components/common/actions/buttons/GxRedRejectButton";
import GxPrimaryButton from "@/app/components/common/actions/buttons/GxPrimaryButton";
import GxHorizontalLine from "@/app/components/common/actions/buttons/GxHorizontalLine";
import BeneficiaryApprovalTable from "./BeneficiaryApprovalTable";
import AppLoader from "@/app/components/webloading/loader/AppLoader";
import { DrawerNoFooter } from "@/app/models/drawer/DrawerNoFooter";
import { RemarksModal } from "@/app/models/dialogs/RemarksModal";
import BeneSuccessModal from "@/app/models/dialogs/BeneSuccessModal";
import BeneficiaryView from "../../beneficiaries/manageBeneficiary/BeneficiaryView";
import DialogXl from "@/app/models/dialogs/DialogXl";
import SearchInput from "@/app/components/common/actions/inputs-b/SearchInput";

interface BeneficiaryApiResponseData {
  beneName?: string | null;
}

interface Access {
  view: boolean;
  approve: boolean;
}

interface BeneficiaryApprovalProp {
  setIsOpenBeneList: (value: boolean) => void;
  isOpenBeneList: boolean;
  access: Access;
  callCount: boolean;
  setCallCount: React.Dispatch<React.SetStateAction<boolean>>;

  // NEW - row-click detail-only entry point (no list dialog opened)
  initialSelectedBeneficiary?: Beneficiary | null;
  initialSelectedBeneficiaryKey?: number;
}

interface SuccessData {
  aprdStatus: "A" | "R" | null;
  beneName: string | null;
}

const BeneficiaryApproval = ({
  isOpenBeneList,
  setIsOpenBeneList,
  access,
  setCallCount,

  initialSelectedBeneficiary,
  initialSelectedBeneficiaryKey,
}: BeneficiaryApprovalProp) => {
  const [isLoading, setIsLoading] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<number[]>(
    [],
  );
  const [checkAll, setCheckAll] = useState(false);
  const [openRemarksModal, setOpenRemarksModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [selectedAction, setSelectedAction] = useState<"A" | "R" | null>(null);
  const [successData, setSuccessData] = useState<SuccessData>({
    aprdStatus: null,
    beneName: null,
  });
  const [selectedBeneficiary, setSelectedBeneficiary] =
    useState<Beneficiary | null>(null);
  const [openDetailsDrawer, setOpenDetailsDrawer] = useState(false);
  const [countryList, setCountryList] = useState<GlobalCountry[]>([]);
  const [remarksError, setRemarksError] = useState("");
  const handleSearchChange = (value: string) => setSearchQuery(value);

  const filteredBeneficiaries = useMemo(() => {
    if (!searchQuery.trim()) return beneficiaries;

    const lowerQuery = searchQuery.toLowerCase();

    return beneficiaries.filter(
      (bene) =>
        bene.beneName?.toLowerCase().includes(lowerQuery) ||
        bene.beneAccountNo?.toLowerCase().includes(lowerQuery) ||
        bene.beneNickname?.toLowerCase().includes(lowerQuery) ||
        bene.beneBankName?.toLowerCase().includes(lowerQuery),
    );
  }, [beneficiaries, searchQuery]);

  const fetchBeneficiaries = async () => {
    setIsLoading(true);
    try {
      const payload = {
        status: "3015",
        pageNo: 1,
        sortBy: "ASC",
        pageSize: 10,
      };
      const response = await gxBeneficiaryPendingListApi(payload);
      const responseData = Array.isArray(response?.data)
        ? response.data
        : [response.data];
      setBeneficiaries(responseData);
    } catch (error) {
      console.error("Error fetching beneficiaries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const payload = { searchText: "", type: "countryName" };
        const response = await GxBeneficiaryCountryApi(payload);

        if (Array.isArray(response?.data)) {
          setCountryList(response.data);
        } else {
          setCountryList([]);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
        setCountryList([]);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (isOpenBeneList) fetchBeneficiaries();
  }, [isOpenBeneList]);

  const handleCloseDialog = () => {
    setIsOpenBeneList(false);
    setCallCount(true);
  };

  const handleRowClick = (event: any) => {
    const bene = event?.dataItem;
    if (bene) {
      setSelectedBeneficiary(bene);
      setOpenDetailsDrawer(true);
    }
  };

  // === Single Approve/Reject ================================================

  const approveBeneficiary = async (
    aprdStatus: "A" | "R",
    remarksText?: string,
  ) => {
    if (!selectedBeneficiary) return;
    setIsLoading(true);
    try {
      const payload: any = {
        beneId: selectedBeneficiary.beneId,
        aprdStatus,
        ...(remarksText && { remarks: remarksText }),
      };
      const result = await gxBeneficiarySingleApproveApi(payload);
      if (result?.status === 1) {
        const responseData = result?.data as BeneficiaryApiResponseData;

        setSuccessData({
          aprdStatus,
          beneName: responseData?.beneName ?? null,
        });
        setOpenSuccessModal(true);
        fetchBeneficiaries();

        setCallCount(true);
      }
    } catch (err) {
      // console.error("Error approving beneficiary:", err);
    } finally {
      setIsLoading(false);
      setOpenDetailsDrawer(false);
    }
  };

  const handleApproveBeneficiary = () => {
    setSelectedAction("A");
    approveBeneficiary("A");
  };

  const handleRejectBeneficiary = () => {
    setSelectedAction("R");
    setOpenRemarksModal(true);
  };

  const submitRejectSingle = () => {
    if (selectedAction === "R" && !remarks.trim()) {
      setRemarksError("Reject reason is required");
      return;
    }

    setRemarksError("");

    approveBeneficiary("R", remarks);
    setRemarks("");
    setOpenRemarksModal(false);
  };

  // === Bulk Approve/Reject ================================================

  const bulkApproveOrReject = async (
    aprdStatus: "A" | "R",
    remarksText?: string,
  ) => {
    if (selectedBeneficiaries.length === 0) return;
    setIsLoading(true);
    try {
      const payload: any = {
        beneId: selectedBeneficiaries,
        aprdStatus,
        ...(remarksText && { remarks: remarksText }),
      };
      const result = await gxBeneficiaryBulkApproveApi(payload);
      if (result?.status === 1 || result?.data !== null) {
        const responseData = result?.data as BeneficiaryApiResponseData;

        setSuccessData({
          aprdStatus,
          beneName: responseData?.beneName ?? null,
        });
        setSelectedBeneficiaries([]);
        setCheckAll(false);
        setOpenSuccessModal(true);
        fetchBeneficiaries();

        setCallCount(true);
      }
    } catch (err) {
      // console.error("Error in bulkApproveOrReject:", err);
    } finally {
      setIsLoading(false);
      setOpenRemarksModal(false);
    }
  };

  const handleBulkApprove = () => {
    setSelectedAction("A");
    bulkApproveOrReject("A");
  };

  const handleBulkReject = () => {
    setSelectedAction("R");
    setOpenRemarksModal(true);
  };

  const confirmBulkAction = () => {
    if (selectedAction === "R" && !remarks.trim()) {
      setRemarksError("Reject reason is required");
      return;
    }

    if (selectedAction === "R") bulkApproveOrReject("R", remarks);
    else if (selectedAction === "A") bulkApproveOrReject("A", remarks);

    setRemarksError("");

    approveBeneficiary("R", remarks);
    setRemarks("");
    setOpenRemarksModal(false);
  };

  const handleFooterSubmit = () => {
    if (selectedBeneficiaries.length > 0) {
      handleBulkApprove();
    }
  };

  const handleSuccessModalClose = () => {
    setOpenSuccessModal(false);
    setSuccessData({ aprdStatus: null, beneName: null });
    setCallCount(false);
    setRemarksError("");
    setRemarks("");
  };

  // NEW effect - reuses the exact same drawer state the row-click inside
  // BeneficiaryApprovalTable already used (handleRowClick / openDetailsDrawer)
  useEffect(() => {
    if (initialSelectedBeneficiary) {
      setSelectedBeneficiary(initialSelectedBeneficiary);
      setOpenDetailsDrawer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedBeneficiaryKey]);

  return (
    <>
      {isLoading && <AppLoader />}

      {/* Beneficiary List Dialog */}
      <DialogXl
        open={isOpenBeneList}
        handleClose={handleCloseDialog}
        handleFooterSubmit={handleFooterSubmit}
        title="Beneficiary"
        showFooter={false}
        subtitle="Approval List"
        cancel="Close"
        submit="Approve Selected"
        requiredMessage="Please select at least one beneficiary"
      >
        <div className="gx-approve-required-modal relative">
          <div>
            <div className="mt-3">
              <SearchInput
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                placeholder="Search Beneficiary"
              />
            </div>

            <div className="mt-3">
              <BeneficiaryApprovalTable
                approveAccess={access?.approve}
                showAll={isOpenBeneList}
                onRowClick={handleRowClick}
                selectedBeneficiaries={selectedBeneficiaries}
                setSelectedBeneficiaries={setSelectedBeneficiaries}
                checkAll={checkAll}
                setCheckAll={setCheckAll}
                beneficiaries={filteredBeneficiaries}
                countryList={countryList}
              />
            </div>
          </div>

          {selectedBeneficiaries.length > 0 && access?.approve && (
            <div className="gx-dialog-actions mt-4">
              <GxHorizontalLine />
              <div className="flex space-x-2 mt-4">
                <GxRedRejectButton onClick={handleBulkReject}>
                  Reject
                </GxRedRejectButton>

                <GxPrimaryButton
                  handleSubmit={handleBulkApprove}
                  label="Approve"
                />
              </div>
            </div>
          )}
        </div>
      </DialogXl>

      <DrawerNoFooter
        title="Beneficiary"
        subtitle="Details"
        openDrawer={openDetailsDrawer}
        onClose={() => setOpenDetailsDrawer(false)}
      >
        <div className="p-4 gx-beneficiary-view">
          {selectedBeneficiary && (
            <>
              <BeneficiaryView
                rowdata={selectedBeneficiary}
                showTransactions={false}
              />
              {access?.approve && (
                <div className="gx-footer-drawer-container">
                  <div className="flex justify-between w-full mt-4">
                    <GxRedRejectButtonSmall onClick={handleRejectBeneficiary}>
                      Reject
                    </GxRedRejectButtonSmall>

                    <GxPrimarySmallButton
                      handleSubmit={handleApproveBeneficiary}
                      label="Approve"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DrawerNoFooter>

      {openRemarksModal && (
        <RemarksModal
          isOpen={openRemarksModal}
          onClose={() => {
            setOpenRemarksModal(false);
            setRemarksError("");
            setRemarks("");
          }}
          errors={{ note: { message: remarksError } }}
          setValue={() => {}}
          isReject={selectedAction === "R"}
          value={remarks}
          onChange={(value) => {
            setRemarks(value);
            if (value.trim()) {
              setRemarksError("");
            }
          }}
          submit={
            selectedBeneficiaries.length > 0
              ? confirmBulkAction
              : submitRejectSingle
          }
        />
      )}

      {openSuccessModal && (
        <BeneSuccessModal
          isOpen={openSuccessModal}
          onClose={handleSuccessModalClose}
          successData={{
            aprdStatus: successData.aprdStatus ?? "A",
            beneName: successData.beneName,
          }}
        />
      )}
    </>
  );
};

export default BeneficiaryApproval;
